<script lang="ts" module>
  export type ButtonVariant =
    | 'primary'
    | 'secondary'
    | 'ghost'
    | 'subtle'
    | 'danger'
    | 'danger-ghost'
    | 'danger-soft'
    | 'pause'
    | 'success'
    | 'inverse'
    | 'inverse-outline';
  export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';
</script>

<script lang="ts">
  import Kbd from './Kbd.svelte';
  // Pill button, as on timepeak.nl.
  //   primary          the one main action of a view (brand fill)
  //   secondary        other actions (outlined)
  //   ghost / subtle   low-emphasis actions (toolbar, "cancel") / tinted brand
  //   danger           destructive confirm (red fill); danger-ghost: low-emphasis
  //                    destructive (text only, "Discard"); danger-soft: tinted (Stop)
  //   pause            amber tinted (Pause timer)
  //   success          positive bulk action ("Mark paid")
  //   inverse / inverse-outline   on the blue brand surface (BrandBanner)
  // Sizes: sm (toolbars), md (default), lg (forms, auth), xl (timer controls).
  // With `href` it renders an <a>. `kbd` shows a keyboard hint at the end.
  import type { Snippet } from 'svelte';
  import type { HTMLButtonAttributes } from 'svelte/elements';
  import Spinner from './Spinner.svelte';

  interface Props extends HTMLButtonAttributes {
    variant?: ButtonVariant;
    size?: ButtonSize;
    href?: string;
    loading?: boolean;
    block?: boolean;
    /** Keyboard shortcut hint, e.g. "Space" or "⌘K". */
    kbd?: string;
    icon?: Snippet;
    iconRight?: Snippet;
    children?: Snippet;
  }

  let { variant = 'secondary', size = 'md', href, loading = false, block = false, kbd, icon, iconRight, children, disabled, type = 'button', class: cls = '', ...rest }: Props = $props();
  const spinner = $derived(size === 'sm' ? 14 : size === 'xl' ? 18 : 16);
</script>

{#snippet content()}
  {#if loading}<span class="btn-icon"><Spinner size={spinner} /></span>{:else if icon}<span class="btn-icon">{@render icon()}</span>{/if}
  {#if children}<span class="btn-label">{@render children()}</span>{/if}
  {#if iconRight}<span class="btn-icon">{@render iconRight()}</span>{/if}
  {#if kbd}<Kbd tone="inherit">{kbd}</Kbd>{/if}
{/snippet}

{#if href}
  <a {href} class="btn btn-{variant} btn-{size} {cls}" class:block aria-disabled={disabled || undefined} {...rest as Record<string, unknown>}>
    {@render content()}
  </a>
{:else}
  <button {type} class="btn btn-{variant} btn-{size} {cls}" class:block class:loading disabled={disabled || loading} aria-busy={loading || undefined} {...rest}>
    {@render content()}
  </button>
{/if}

<style>
  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-2);
    height: var(--control-h);
    padding: 0 var(--space-4);
    border-radius: var(--radius-button);
    border: 1px solid transparent;
    font-weight: var(--weight-semibold);
    font-size: var(--text-base);
    line-height: var(--leading-none);
    white-space: nowrap;
    text-decoration: none;
    transition: background var(--dur) var(--ease), border-color var(--dur), color var(--dur), box-shadow var(--dur), transform var(--dur-fast);
    user-select: none;
    -webkit-tap-highlight-color: transparent;
    min-width: 0;
  }
  .btn:hover { text-decoration: none; }
  .btn:active:not(:disabled) { transform: translateY(0.5px) scale(0.99); }
  .btn:focus-visible { outline: none; box-shadow: var(--ring); }
  .btn:disabled, .btn[aria-disabled='true'] { opacity: 0.55; pointer-events: none; }
  .block { width: 100%; }
  .btn-label { min-width: 0; overflow: hidden; text-overflow: ellipsis; line-height: var(--leading-tight); }
  .btn-icon { display: inline-flex; flex-shrink: 0; }
  .btn-icon :global(svg) { width: var(--icon-md); height: var(--icon-md); }

  /* Sizes */
  .btn-sm { height: var(--control-h-sm); padding: 0 var(--space-3); font-size: var(--text-sm); gap: var(--space-1_5); }
  .btn-sm .btn-icon :global(svg) { width: var(--icon-sm); height: var(--icon-sm); }
  .btn-lg { height: var(--control-h-lg); padding: 0 var(--space-6); font-size: var(--text-md); }
  .btn-lg .btn-icon :global(svg) { width: var(--icon-lg); height: var(--icon-lg); }
  .btn-xl { height: var(--control-h-xl); padding: 0 var(--space-8); font-size: var(--text-lg); gap: var(--space-2_5); border-width: 1.5px; }
  .btn-xl .btn-icon :global(svg) { width: var(--icon-xl); height: var(--icon-xl); }

  /* Variants */
  .btn-primary { background: var(--brand); color: var(--on-brand); }
  .btn-primary:hover { background: var(--brand-strong); box-shadow: var(--shadow-brand); transform: translateY(-1px); }
  .btn-secondary { background: var(--surface); color: var(--text); border: 1.5px solid var(--border); }
  .btn-secondary:hover { border-color: var(--brand); color: var(--brand); }
  .btn-ghost { background: transparent; color: var(--text-2); }
  .btn-ghost:hover { background: var(--surface-hover); color: var(--text); }
  .btn-subtle { background: var(--brand-soft); color: var(--brand); }
  .btn-subtle:hover { background: var(--brand-tint); }
  .btn-danger { background: var(--danger); color: var(--on-brand); }
  .btn-danger:hover { background: var(--danger-strong); }
  :global([data-theme='dark']) .btn-danger:hover { background: color-mix(in srgb, var(--danger) 85%, var(--bg)); }
  .btn-danger-ghost { background: transparent; color: var(--danger); }
  .btn-danger-ghost:hover { background: var(--danger-soft); color: var(--danger-strong); }
  .btn-danger-soft { background: var(--danger-soft); color: var(--danger-strong); border-color: color-mix(in srgb, var(--danger) 25%, transparent); }
  .btn-danger-soft:hover { background: color-mix(in srgb, var(--danger) 18%, transparent); }
  .btn-pause { background: var(--pause-soft); color: var(--pause-strong); border-color: color-mix(in srgb, var(--pause) 35%, transparent); }
  .btn-pause:hover { background: color-mix(in srgb, var(--pause) 24%, transparent); }
  .btn-success { background: var(--success); color: var(--on-brand); }
  .btn-success:hover { background: var(--success-strong); }
  :global([data-theme='dark']) .btn-success:hover { background: color-mix(in srgb, var(--success) 85%, var(--bg)); }
  .btn-inverse { background: var(--on-brand); color: var(--brand-600); }
  .btn-inverse:hover { background: var(--brand-50); box-shadow: var(--shadow-inverse); transform: translateY(-1px); }
  .btn-inverse-outline { background: transparent; color: var(--on-brand); border: 1.5px solid var(--on-brand-3); }
  .btn-inverse-outline:hover { border-color: var(--on-brand); background: var(--on-brand-hover); }
  .btn-inverse:focus-visible, .btn-inverse-outline:focus-visible { box-shadow: 0 0 0 3px var(--on-brand-3); }
</style>
